import React, { useState, useEffect, useRef } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { teacherConfirmAPI } from '../lib/api';
import { eingabeVerlassen, leinwandAnpassen } from '../lib/unterschriftLeinwand';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle2, Loader2, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

var DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function formatDayRow(d, idx) {
  var dt = new Date(d.date + 'T12:00:00');
  var dayLabel = DAY_NAMES[dt.getDay()];
  var dd = String(dt.getDate()).padStart(2, '0');
  var mm = String(dt.getMonth() + 1).padStart(2, '0');
  return {
    key: d.date + '-' + idx,
    dateStr: d.is_continuation ? '' : dayLabel + ', ' + dd + '.' + mm + '.',
    start: d.start_time || '\u2013',
    end: d.end_time || '\u2013',
    hours: (d.hours || 0).toFixed(2),
    kindKrank: !!d.kind_krank,
    isContinuation: !!d.is_continuation,
  };
}

function WorkedDayRow({ row }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="py-1.5 px-3 text-slate-700">
        <span translate="no">{row.dateStr}</span>
        {row.kindKrank && !row.isContinuation && (
          <span className="block text-xs font-medium text-amber-700">Kind abwesend</span>
        )}
      </td>
      <td className="py-1.5 px-3 text-slate-600" translate="no">{row.start}</td>
      <td className="py-1.5 px-3 text-slate-600" translate="no">{row.end}</td>
      <td className="py-1.5 px-3 text-right font-medium text-slate-800" translate="no">{row.hours}</td>
    </tr>
  );
}

// Auftrag LK1: Fehlertext je nach Antwort des Servers
function fehlerZustand(err) {
  var status = err && err.response ? err.response.status : null;
  if (status === 404) {
    return { titel: 'Link nicht mehr gültig', message: 'Dieser Link gilt nicht mehr. Bitte fordern Sie bei der Person, die Ihnen den Link geschickt hat, einen neuen Link an.' };
  }
  if (status === 410) {
    return { titel: 'Link abgelaufen', message: 'Dieser Link ist abgelaufen. Bitte fordern Sie bei der Person, die Ihnen den Link geschickt hat, einen neuen Link an.' };
  }
  return { titel: 'Seite konnte nicht geladen werden', message: 'Das ist ein technischer Fehler. Ihr Link ist davon nicht betroffen.', neuLaden: true };
}

function ErrorState({ titel, message, neuLaden }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
          <h2 className="text-lg font-semibold text-slate-800" data-testid="teacher-confirm-error-title">{titel}</h2>
          <p className="text-slate-500" data-testid="teacher-confirm-error">{message}</p>
          {neuLaden && (
            <Button variant="outline" onClick={function neu() { window.location.reload(); }} data-testid="teacher-confirm-reload">Neu laden</Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SuccessState({ schonUnterschrieben }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <Card className="w-full max-w-lg">
        <CardContent className="p-8 text-center space-y-4" data-testid="teacher-confirm-success">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
          {schonUnterschrieben ? (
            <>
              <h2 className="text-lg font-semibold text-slate-800">Bereits unterschrieben</h2>
              <p className="text-slate-500">Dieser Stundenzettel ist bereits unterschrieben. Sie müssen nichts mehr tun.</p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-slate-800">Vielen Dank!</h2>
              <p className="text-slate-500">Die Arbeitszeiten wurden unterschrieben und bestätigt. Sie können diese Seite jetzt schließen.</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg"><CardContent className="p-8 space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-32 w-full" />
      </CardContent></Card>
    </div>
  );
}

function InfoBlock({ data }) {
  return (
    <div className="space-y-1 text-sm">
      <p><span className="text-slate-500">Mitarbeiter/in:</span> <span className="font-medium text-slate-800">{data.employee_name}</span></p>
      <p><span className="text-slate-500">Kind:</span> <span className="font-medium text-slate-800">{data.child_name}</span></p>
      <p><span className="text-slate-500">Schule:</span> <span className="font-medium text-slate-800">{data.school_name}</span></p>
      <p><span className="text-slate-500">Zeitraum:</span> <span className="font-medium text-slate-800" translate="no">{data.month} {data.year}</span></p>
    </div>
  );
}

function WorkedDaysTable({ rows, anwesend, kindAbwesend, gesamt }) {
  var rowElements = rows.map(function renderRow(r) { return <WorkedDayRow key={r.key} row={r} />; });
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500" data-testid="teacher-confirm-hinweis">
        An den mit <span className="font-medium text-amber-700">Kind abwesend</span> gekennzeichneten Tagen war das
        Kind nicht in der Schule. Diese Zeiten sind mit dem Jugendamt vereinbart und werden vergütet.
      </p>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="teacher-confirm-table">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="py-2 px-3 text-left font-medium">Datum</th>
              <th className="py-2 px-3 text-left font-medium">Von</th>
              <th className="py-2 px-3 text-left font-medium">Bis</th>
              <th className="py-2 px-3 text-right font-medium">Std.</th>
            </tr>
          </thead>
          <tbody>{rowElements}</tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50">
              <td colSpan={3} className="py-2 px-3 text-slate-700">Anwesenheitsstunden (von der Lehrkraft bestätigt)</td>
              <td className="py-2 px-3 text-right font-medium text-slate-800" data-testid="teacher-confirm-anwesend" translate="no">{anwesend.toFixed(2)}</td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan={3} className="py-2 px-3 text-slate-700">Stunden bei Abwesenheit des Kindes</td>
              <td className="py-2 px-3 text-right font-medium text-slate-800" data-testid="teacher-confirm-kind-abwesend" translate="no">{kindAbwesend.toFixed(2)}</td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td colSpan={3} className="py-2 px-3 font-semibold text-slate-700">Gesamtstunden</td>
              <td className="py-2 px-3 text-right font-bold text-slate-900" data-testid="teacher-confirm-total" translate="no">{gesamt.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ConfirmForm({ teacherName, setTeacherName, confirming, handleConfirm, sigRef, signature, setSignature, clearSig }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm">Ihr Name</Label>
        <Input
          value={teacherName}
          onChange={function onChange(e) { setTeacherName(e.target.value); }}
          placeholder="z.B. Frau Müller"
          className="mt-1"
          data-testid="teacher-confirm-name-input"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-sm">Ihre Unterschrift</Label>
          <Button variant="ghost" size="sm" onClick={clearSig} data-testid="teacher-confirm-clear-signature">
            <X className="h-4 w-4 mr-1" />Löschen
          </Button>
        </div>
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-1">
          <SignatureCanvas
            ref={sigRef}
            clearOnResize={false}
            canvasProps={{ className: 'w-full bg-white rounded', style: { width: '100%', height: '160px', touchAction: 'none' } }}
            onBegin={function onBegin() { eingabeVerlassen(); }}
            onEnd={function onEnd() { if (sigRef.current) setSignature(sigRef.current.toDataURL()); }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">Mit dem Finger (Handy/Tablet) oder der Maus (Laptop/PC) unterschreiben</p>
      </div>
      <Button
        className="w-full bg-blue-800 hover:bg-blue-900"
        onClick={handleConfirm}
        disabled={confirming || !teacherName.trim() || !signature}
        data-testid="teacher-confirm-submit-button"
      >
        {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
        Unterschreiben & bestätigen
      </Button>
    </div>
  );
}

var TeacherConfirm = function TeacherConfirm() {
  var { token } = useParams();
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState(null);
  var [teacherName, setTeacherName] = useState('');
  var [confirming, setConfirming] = useState(false);
  var [confirmed, setConfirmed] = useState(false);
  var [schonUnterschrieben, setSchonUnterschrieben] = useState(false);
  var sigRef = useRef(null);
  var [signature, setSignature] = useState('');

  var clearSig = function clearSig() {
    if (sigRef.current) { sigRef.current.clear(); setSignature(''); }
  };

  useEffect(function loadData() {
    teacherConfirmAPI.get(token)
      .then(function onOk(res) {
        setData(res.data);
        if (res.data.already_confirmed) { setSchonUnterschrieben(true); setConfirmed(true); }
      })
      .catch(function onErr(err) { setError(fehlerZustand(err)); })
      .finally(function onDone() { setLoading(false); });
  }, [token]);

  // US1: Zeichenfläche nur bei echter Größenänderung neu vermessen (Bild bleibt stehen).
  useEffect(function leinwandMitwachsen() {
    var beiGroesse = function beiGroesse() { leinwandAnpassen(sigRef.current); };
    window.addEventListener('resize', beiGroesse);
    return function aufraeumen() { window.removeEventListener('resize', beiGroesse); };
  }, []);

  var handleConfirm = async function handleConfirm() {
    if (!teacherName.trim()) { toast.error('Bitte geben Sie Ihren Namen ein'); return; }
    if (!signature) { toast.error('Bitte unterschreiben Sie im Unterschriftsfeld'); return; }
    try {
      setConfirming(true);
      await teacherConfirmAPI.confirm(token, teacherName.trim(), signature);
      setConfirmed(true);
    } catch (err) {
      toast.error(fehlertext(err, 'Fehler bei der Bestätigung'));
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState titel={error.titel} message={error.message} neuLaden={error.neuLaden} />;
  if (confirmed) return <SuccessState schonUnterschrieben={schonUnterschrieben} />;

  var rows = (data.worked_days || []).map(formatDayRow);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      <Card className="w-full max-w-lg overflow-hidden" data-testid="teacher-confirm-page">
        <div style={{ background:'#ffffff', borderBottom:'2px solid #1e3a5f' }} className="flex items-center justify-between px-6 py-4">
          {data.logo_data
            ? <img src={data.logo_data} alt="Das Relationship" style={{ maxHeight:40, maxWidth:170, objectFit:'contain' }} />
            : <span style={{ color:'#1e3a5f', fontWeight:800, fontSize:20 }}>Das Relationship</span>}
          <span style={{ color:'#1e3a5f', fontWeight:600, fontSize:16 }}>Schulbegleitung</span>
        </div>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="text-xl font-bold text-slate-900">Stundenzettel unterschreiben</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <InfoBlock data={data} />
          <WorkedDaysTable
            rows={rows}
            anwesend={data.stunden_anwesend ?? data.total_hours ?? 0}
            kindAbwesend={data.stunden_kind_abwesend ?? 0}
            gesamt={data.stunden_gesamt ?? data.total_hours ?? 0}
          />
          <ConfirmForm
            teacherName={teacherName}
            setTeacherName={setTeacherName}
            confirming={confirming}
            handleConfirm={handleConfirm}
            sigRef={sigRef}
            signature={signature}
            setSignature={setSignature}
            clearSig={clearSig}
          />
        </CardContent>
        <div style={{ background:'#f8fafc', borderTop:'1px solid #e2e8f0' }} className="px-6 py-4 text-center">
          <p className="text-xs font-semibold text-slate-600 mb-1">Das Relationship – Schulbegleitung</p>
          <p className="text-xs text-slate-400 mb-2">Bei Fragen: schulbegleitung@dasrelationship.com</p>
          <a href="https://dasrelationship.com/impressum" target="_blank" rel="noreferrer" style={{ color:'#1e3a5f' }} className="text-xs font-semibold">Impressum</a>
          <span className="text-slate-300 mx-2">·</span>
          <a href="https://dasrelationship.com/datenschutzerklaerung" target="_blank" rel="noreferrer" style={{ color:'#1e3a5f' }} className="text-xs font-semibold">Datenschutzerklärung</a>
        </div>
      </Card>
    </div>
  );
};

export default TeacherConfirm;
